/**
 * 0226 박현민
 * 엔드포인트 기점으로 구조화
 * Basekey는 스웨거 기준으로 BASE 넣고 인자는 추가하는 식으로 하시죠
 * 사용예시: QUERY_KEYS.wanted.adjustment(2026, 1)
 */
export const QUERY_KEYS = {
  // roster 엔드포인트
  roster: {
    latest: (groupId?: string | null) =>
      ["roster", "latest", groupId ?? ""] as const,
    versions: (
      year?: number | null,
      month?: number | null,
      groupId?: string | null
    ) => ["VERSIONS", year ?? "", month ?? "", groupId ?? null] as const,
    versionsScope: (year?: number | null, month?: number | null) =>
      ["VERSIONS", year ?? "", month ?? ""] as const,
    schedule: (scheduleId?: string, groupId?: string) => [
      "ROSTER_SCHEDULE",
      scheduleId,
      groupId,
    ],
    scheduleScope: () => ["ROSTER_SCHEDULE"] as const,
    /** GET /roster/issued_roster — 마감 스냅샷 (useSnapShot 등). `groupId`는 쿼리 `group_id`와 동일 */
    issuedRosterSnapshot: (year: number, month: number, groupId?: string) => [
      "ROSTER_ISSUED_ROSTER",
      year,
      month,
      groupId ?? "",
    ],
    issuedRosterSnapshotScope: (year?: number | null, month?: number | null) =>
      ["ROSTER_ISSUED_ROSTER", year ?? "", month ?? ""] as const,
    issuedList: (groupId?: string | null) =>
      ["ISSUED", groupId ?? undefined] as const,
    /** GET /roster/issued_roster/me — 로그인 사용자 본인 행만 (useMyIssuedRoster) */
    myIssuedRoster: (year: number, month: number) => [
      "ROSTER_ISSUED_ROSTER_ME",
      year,
      month,
    ],
    prevTail: (
      year: number,
      month: number,
      scheduleId?: string,
      tailDays?: number,
      groupId?: string
    ) => ["ROSTER_PREV_TAIL", year, month, scheduleId, tailDays, groupId],
    prevTailScope: (year?: number | null, month?: number | null) =>
      ["ROSTER_PREV_TAIL", year ?? "", month ?? ""] as const,
    jobStatus: () => ["rosterJobStatus"] as const,
  },
  // nurses 엔드포인트
  nurses: {
    scope: () => ["NURSES"] as const,
    list: (
      groupId?: string | null,
      year?: number | null,
      month?: number | null
    ) => ["NURSES", groupId ?? "", year ?? "", month ?? ""] as const,
    listScope: (groupId?: string | null) => ["NURSES", groupId ?? ""] as const,
    members: (
      groupId?: string | null,
      year?: number | null,
      month?: number | null
    ) => ["NURSES", "members", groupId ?? "", year ?? "", month ?? ""] as const,
    membersScope: (groupId?: string | null) =>
      ["NURSES", "members", groupId ?? ""] as const,
    detail: (
      nurseId: string,
      groupId?: string | null,
      year?: number | null,
      month?: number | null
    ) =>
      [
        "NURSES",
        "detail",
        nurseId,
        groupId ?? "",
        year ?? "",
        month ?? "",
      ] as const,
    dispatchHistory: (accountId: string, officeId: string, nurseId: string, groupId: string) =>
      ["NURSES", "detail", nurseId, "dispatch-history", accountId, officeId, groupId, "all"] as const,
    detailScope: () => ["NURSES", "detail"] as const,
    monthlyLimits: (groupId?: string | null, nurseId?: string | null) =>
      ["NURSES", "monthly-limits", groupId ?? "", nurseId ?? ""] as const,
    monthlyLimitsScope: (groupId?: string | null) =>
      ["NURSES", "monthly-limits", groupId ?? ""] as const,
    personnelBasicInfo: () => ["personnel-basic-info"],
    fullSchema: (groupId?: string | null) =>
      ["nurses", "fullSchema", groupId ?? undefined] as const,
    precepteePeriods: (
      groupId?: string | null,
      year?: number | null,
      month?: number | null
    ) => ["preceptee-periods", (groupId ?? "").trim(), year, month] as const,
    precepteePeriodsScope: () => ["preceptee-periods"] as const,
  },
  /** GET /groups — 관리자 그룹(병동) 목록 */
  groups: {
    list: () => ["groups", "list"] as const,
    /** GET /groups/by-office?office_id= — 병동 목록(+ hn_id는 마스터/HN 일부만) */
    byOffice: (officeId: string) => ["groups", "by-office", officeId] as const,
    /** invalidateQueries — 위 쿼리 office_id 무관 일괄 무효화 */
    byOfficeScope: () => ["groups", "by-office"] as const,
  },
  // wanted 엔드포인트
  wanted: {
    adjustment: (year: number, month: number) => [
      "wanted_adjustment",
      year,
      month,
    ],
    all: (groupId?: string) => ["wanted_all", groupId],
    status: (year: number, month: number, groupId?: string) => [
      "wanted_status",
      year,
      month,
      groupId,
    ],
    config: (year: number, month: number, groupId: string = "") => [
      "wanted_config",
      year,
      month,
      groupId,
    ],
    /** GET /wanted/{year}/{month}/shift-requests */
    shiftRequests: (year: number, month: number, groupId?: string) => [
      "wanted_shift_requests",
      year,
      month,
      groupId,
    ],
  },
  // 선호도 (원티드 작성)
  preferences: {
    latest: (year: number, month: number, groupId?: string) => [
      "preferences_latest",
      year,
      month,
      groupId,
    ],
    all: (year: number, month: number, groupId?: string) => [
      "preferences_all",
      year,
      month,
      groupId,
    ],
    schedule: (year: number, month: number, groupId?: string) => [
      "PREFERENCE_SCHEDULE",
      year,
      month,
      groupId,
    ],
    monthlyMemo: (year: number, month: number, groupId?: string) =>
      ["preferences_monthly_memo", year, month, groupId ?? ""] as const,
    monthlyMemoGroup: (year: number, month: number, groupId?: string | null) =>
      ["preferences_monthly_memo_group", year, month, groupId ?? ""] as const,
  },

  /** GET /grade/config */
  grade: {
    config: (groupId?: string | null) => ["GRADE_CONFIG", groupId] as const,
  },
  //  설정
  config: {
    configData: (groupId: string) => ["config", groupId, "current"] as const,
    configVersions: (groupId: string) => ["configVersions", groupId],
    rosterConfigVersions: (groupId: string) => [
      "rosterConfigVersions",
      groupId,
    ],
    rosterConfigVersionsScope: () => ["rosterConfigVersions"] as const,
  },
  auth: {
    switchGroupCandidates: () => ["auth", "switch-group", "candidates"],
  },
  shifts: {
    list: (groupId?: string) => ["SHIFTS", groupId],
    shiftManagerScope: (groupId?: string) => ["shiftManager", groupId] as const,
    shiftManager: (groupId?: string, configVersion: string = "default") => [
      "shiftManager",
      groupId,
      configVersion,
    ],
  },
  manpower: {
    monthly: (
      officeId?: string | null,
      groupId?: string | null,
      year?: number | null,
      month?: number | null
    ) =>
      [
        "manpower",
        officeId ?? undefined,
        groupId ?? undefined,
        year ?? undefined,
        month ?? undefined,
      ] as const,
    scope: () => ["manpower"] as const,
  },
  // 공휴일
  dates: {
    holidays: (year: number, month: number) => ["dates_holidays", year, month],
  },
  // 문의하기 (contact/list, contact/write 무효화용)
  contact: {
    list: (page: number, pagesize: number) => ["contact_list", page, pagesize],
  },
  // 공지사항 (contact/notice/list, contact/notice/detail)
  notice: {
    list: (page: number, pagesize: number) => ["notice_list", page, pagesize],
    detail: (noticeId: number) => ["notice_detail", noticeId],
  },
  // 알림
  alarms: {
    count: () => ["alarms", "count"],
    list: () => ["alarms", "list"],
    listPopover: () => ["alarms", "list", "popover"],
    listModal: () => ["alarms", "list", "modal"],
  },
  // 주휴
  weeklyOff: {
    scope: () => ["WEEKLYOFF"] as const,
    my: (year: number, month: number, nurseId?: string) => [
      "weeklyOff_my",
      year,
      month,
      nurseId,
    ],
  },
} as const;
